import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-otp-input',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './otp-input.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => OtpInputComponent), multi: true },
    ],
    styles: [`
        :host { display: block; }
        input { caret-color: #022D35; }
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    `],
})
export class OtpInputComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
    @Input() length = 6;
    @Input() autoSubmit = true;
    @Input() autoFocus = true;
    @Input() error = false;
    // Use a visible gray border (for white surfaces like cards/dialogs). Default = white border (auth on gray bg).
    @Input() bordered = false;
    @Output() complete = new EventEmitter<string>();

    @ViewChildren('digitInput') private inputs!: QueryList<ElementRef<HTMLInputElement>>;

    digits: string[] = [];
    isDisabled = false;

    private onChange: (v: string) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['length']) this.digits = Array(this.length).fill('');
    }

    ngAfterViewInit(): void {
        if (this.autoFocus) setTimeout(() => this.inputs.first?.nativeElement.focus(), 0);
    }

    writeValue(value: string | number | null): void {
        const v = (value ?? '').toString();
        this.digits = Array(this.length).fill('').map((_, i) => v[i] ?? '');
    }
    registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }
    setDisabledState(isDisabled: boolean): void { this.isDisabled = isDisabled; }

    onInput(event: Event, idx: number): void {
        const input = event.target as HTMLInputElement;
        const raw = (input.value || '').replace(/\D/g, '');
        if (raw.length > 1) {
            this.distribute(raw, idx);
            return;
        }
        this.digits[idx] = raw[0] ?? '';
        input.value = this.digits[idx];
        this.emit();
        if (raw.length === 1 && idx < this.length - 1) this.focusAt(idx + 1);
        this.maybeAutoSubmit();
    }

    onKeyDown(event: KeyboardEvent, idx: number): void {
        if (event.key === 'Backspace' && !this.digits[idx] && idx > 0) {
            event.preventDefault();
            this.focusAt(idx - 1);
            this.digits[idx - 1] = '';
            this.emit();
        } else if (event.key === 'ArrowLeft' && idx > 0) {
            event.preventDefault();
            this.focusAt(idx - 1);
        } else if (event.key === 'ArrowRight' && idx < this.length - 1) {
            event.preventDefault();
            this.focusAt(idx + 1);
        }
    }

    onPaste(event: ClipboardEvent, idx: number): void {
        event.preventDefault();
        const text = event.clipboardData?.getData('text') ?? '';
        const digits = text.replace(/\D/g, '');
        if (digits) this.distribute(digits, idx);
    }

    onFocus(event: FocusEvent): void {
        (event.target as HTMLInputElement).select?.();
        this.onTouched();
    }

    private distribute(raw: string, startIdx: number): void {
        for (let i = 0; i < raw.length && startIdx + i < this.length; i++) {
            this.digits[startIdx + i] = raw[i];
        }
        this.emit();
        const lastFilled = Math.min(startIdx + raw.length - 1, this.length - 1);
        this.focusAt(lastFilled);
        this.maybeAutoSubmit();
    }

    private focusAt(idx: number): void {
        const el = this.inputs.toArray()[idx]?.nativeElement;
        el?.focus();
        el?.select?.();
    }

    private emit(): void {
        this.onChange(this.digits.join(''));
    }

    private maybeAutoSubmit(): void {
        if (!this.autoSubmit) return;
        if (this.digits.length === this.length && this.digits.every(d => d !== '')) {
            this.complete.emit(this.digits.join(''));
        }
    }
}
